/**
 * Electrical Zone Calculator for Sauna Wiring
 *
 * Validates wiring types based on component height (Y-position) per
 * SEK Svensk Elstandard. Standard PVC cable (EKK/FKX) melts at ~70°C,
 * causing short circuits and fire hazards in hot zones.
 *
 * Zone 1 (Hot): above threshold → SiHF 180°C silicone cable required
 * Zone 2 (Cold): below threshold → standard cable permitted
 *
 * All thresholds and text are configurable via Rule Registry.
 */
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';

// ─── Types ──────────────────────────────────────────────────

export type ElectricalComponentNameKey = 'term_el_ceilingSpots' | 'term_el_wallLightHigh' | 'term_el_heaterConnection' | 'term_el_ledStripLow';
export type ElectricalPlacementWall = 'front' | 'left' | 'right';
type ElectricalEntryWall = 'short' | 'long';
type ElectricalSaunaPosition = 'left' | 'right';

export interface ElectricalComponent {
    /** Display name, e.g. "Takspots", "Bastuaggregat" (legacy Swedish) */
    name: string;
    /** Translation key for the display name — resolves via t[nameKey] */
    nameKey: ElectricalComponentNameKey;
    /** Height from floor in millimetres */
    yPosMm: number;
    /** Component category for specialised rules */
    category: 'lighting' | 'heater' | 'transformer' | 'other';
    /** Resolved wall placement for entry-aware layouts. Omitted for legacy defaults. */
    placementWall?: ElectricalPlacementWall;
    /** Resolved room placement for entry-aware layouts. Omitted for legacy defaults. */
    room?: 'sauna' | 'relax';
}

export interface ElectricalBomRow {
    /** Item description, e.g. "Takspots Kabel" (legacy Swedish) */
    item: string;
    /** Translation key for the component name part */
    nameKey: ElectricalComponentNameKey;
    /** Recommended cable type string */
    cableType: string;
    /** Translation key for cable type — resolves via t[cableTypeKey] */
    cableTypeKey: 'term_el_siliconeCable' | 'term_el_standardCable';
    /** Which thermal zone this belongs to */
    zone: 'hot' | 'cold';
    /** Optional safety warning (legacy Swedish) */
    warning?: string;
    /** Translation key for warning */
    warningKey?: 'term_el_hotZoneWarning' | 'term_el_transformerWarning';
    /** Resolved wall placement for entry-aware layouts. Omitted for legacy defaults. */
    placementWall?: ElectricalPlacementWall;
    /** Resolved room placement for entry-aware layouts. Omitted for legacy defaults. */
    room?: 'sauna' | 'relax';
}

export interface ElectricalValidationResult {
    /** Full electrical BOM with correct cable types */
    bom: ElectricalBomRow[];
    /** All safety warnings produced (legacy Swedish) */
    warnings: string[];
    /** Translation keys for warnings */
    warningKeys: Array<'term_el_hotZoneWarning' | 'term_el_transformerWarning'>;
    /** True if any component sits in the hot zone */
    hasHotZoneComponents: boolean;
}

interface ElectricalComponentOptions {
    entryWall?: ElectricalEntryWall;
    saunaPosition?: ElectricalSaunaPosition;
    hasRelaxRoom?: boolean;
    /**
     * A wood stove has no electrical connection. Absent means electric, the
     * pre-existing behaviour for callers that do not know the heater.
     */
    heaterType?: 'electric' | 'wood';
}

function getServiceWall(
    entryWall: ElectricalEntryWall = 'short',
    saunaPosition: ElectricalSaunaPosition = 'left',
): ElectricalPlacementWall {
    if (entryWall === 'short') return 'front';
    return saunaPosition === 'left' ? 'right' : 'left';
}

function getWallLabel(wall: ElectricalPlacementWall): string {
    if (wall === 'front') return 'kortvägg fram';
    return wall === 'right' ? 'höger långvägg' : 'vänster långvägg';
}

function appendPlacementWarning(
    warning: string,
    placementWall?: ElectricalPlacementWall,
): string {
    return placementWall ? `${warning} Placering: ${getWallLabel(placementWall)}.` : warning;
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Validate a list of electrical components by their Y-position and
 * return the correct cable type per SEK Svensk Elstandard.
 *
 * All thresholds and labels are resolved from the Rule Registry,
 * allowing superuser overrides without code changes.
 */
export function validateElectricalWiring(
    components: ElectricalComponent[],
    overrides: RuleOverrides = {},
): ElectricalValidationResult {
    const threshold = getRuleValue<number>('electrical.hotZoneThresholdMm', overrides);
    const siliconeCable = getRuleValue<string>('electrical.siliconeCableType', overrides);
    const standardCable = getRuleValue<string>('electrical.standardCableType', overrides);
    const hotZoneWarning = getRuleValue<string>('electrical.hotZoneWarning', overrides);
    const transformerWarning = getRuleValue<string>('electrical.transformerWarning', overrides);

    const bom: ElectricalBomRow[] = [];
    const warnings: string[] = [];
    const warningKeySet = new Set<'term_el_hotZoneWarning' | 'term_el_transformerWarning'>();
    let hasHotZoneComponents = false;

    for (const comp of components) {
        // The heater feed terminates at the heater's junction box: heater
        // manufacturers require heat-resistant cable for that run regardless
        // of mounting height, so it can never ride the cold-zone rule.
        const isHotZone = comp.yPosMm > threshold || comp.category === 'heater';

        if (isHotZone) {
            const warning = appendPlacementWarning(hotZoneWarning, comp.placementWall);
            hasHotZoneComponents = true;
            bom.push({
                item: `${comp.name} Kabel`,
                nameKey: comp.nameKey,
                cableType: siliconeCable,
                cableTypeKey: 'term_el_siliconeCable',
                zone: 'hot',
                warning,
                warningKey: 'term_el_hotZoneWarning',
                ...(comp.placementWall ? { placementWall: comp.placementWall } : {}),
                ...(comp.room ? { room: comp.room } : {}),
            });
        } else {
            bom.push({
                item: `${comp.name} Kabel`,
                nameKey: comp.nameKey,
                cableType: standardCable,
                cableTypeKey: 'term_el_standardCable',
                zone: 'cold',
                ...(comp.placementWall ? { placementWall: comp.placementWall } : {}),
                ...(comp.room ? { room: comp.room } : {}),
            });
        }

        // Transformer-specific rule
        if (comp.category === 'transformer' && comp.yPosMm > threshold) {
            warnings.push(transformerWarning);
            warningKeySet.add('term_el_transformerWarning');
        }
    }

    // Deduplicate warnings
    const uniqueWarnings = [...new Set(warnings)];

    return { bom, warnings: uniqueWarnings, warningKeys: [...warningKeySet], hasHotZoneComponents };
}

// ─── Default Component Factory ──────────────────────────────

/**
 * Generate the default set of electrical components for a sauna
 * based on ceiling height. The builder doesn't have a dedicated UI for
 * placing electrical items — we infer them from dimensions.
 */
export function getDefaultElectricalComponents(
    ceilingHeightMm: number = 2100,
    options: ElectricalComponentOptions = {},
): ElectricalComponent[] {
    const serviceWall = getServiceWall(options.entryWall, options.saunaPosition);
    const shouldExposePlacement = options.entryWall === 'long';
    const placement = shouldExposePlacement
        ? {
            doorLight: serviceWall,
            outlet: serviceWall,
            serviceRoom: options.hasRelaxRoom === false ? 'sauna' as const : 'relax' as const,
        }
        : undefined;

    return [
        {
            name: 'Takspots (infälld belysning)',
            nameKey: 'term_el_ceilingSpots',
            yPosMm: ceilingHeightMm,
            category: 'lighting',
        },
        {
            name: 'Vägglampa (hög)',
            nameKey: 'term_el_wallLightHigh',
            yPosMm: Math.round(ceilingHeightMm * 0.75),
            category: 'lighting',
            ...(placement ? { placementWall: placement.doorLight, room: placement.serviceRoom } : {}),
        },
        ...(options.heaterType === 'wood'
            ? []
            : [
                  {
                      name: 'Bastuaggregat anslutning',
                      nameKey: 'term_el_heaterConnection' as const,
                      yPosMm: 200,
                      category: 'heater' as const,
                  },
              ]),
        {
            name: 'LED-strip (låg, under lav)',
            nameKey: 'term_el_ledStripLow',
            yPosMm: 400,
            category: 'lighting',
            ...(placement ? { placementWall: placement.outlet, room: placement.serviceRoom } : {}),
        },
    ];
}
